/* eslint-disable max-lines */
import { Box, RgMatchedFace } from './alarms/alarms';
import { Location } from './location';
import { ABTestConfig, AlarmResolution } from './location-alarms';
import { UnifiedMonitoringProduct } from './unified-monitoring';

export interface MatchedFace {
  profile_id: string;
  employee_id?: string;
  badge_id?: string;
  face_box: number[];
  image_path: string;
  profile_name: string;
  profile_type: string;
  similarity_score: number;
  gender?: string;
  full_body_image_path?: string | null;
  person_box?: number[];
}

/**
 * One object detection (person/vehicle) on a frame. `box` is in the frame's
 * intrinsic pixel space (same space as MatchedFace.face_box).
 */
export interface FrameDetection {
  box: { x1: number; y1: number; x2: number; y2: number };
  name?: string;
  confidence?: number;
  person_id?: string;
  track_id?: string | null;
  frame_idx?: number;
}

export interface Frame {
  id?: string;
  url: string;
  timestamp: number;
  frame_idx?: number;
  matched_faces?: MatchedFace[];
  persons?: FrameDetection[];
  vehicles?: FrameDetection[];
  // Set when many alarms' frames are merged into one continuous track (unified
  // alarm group): identifies the source alarm so face overlays can be scoped to
  // the alarm currently playing. Undefined for single-alarm callers.
  alarmId?: string;
  cameraName?: string;
  alarmType?: string;
}

export interface AlarmVideo {
  boxes: Box[];
  url: string;
  startTime: string;
  endTime: string;
  matchedFaces?: RgMatchedFace[];
}

export interface Media {
  type: string;
  video?: AlarmVideo;
  frames?: Frame[];
}

export interface UnifiedAlarm {
  id: string;
  alarmGroupId: string;
  timestamp: string;
  cameraId: string;
  cameraName: string;
  cameraGroupId: string;
  cameraGroupName: string;
  cameraLiveStreamUrl: string;
  cameraPlayBackUrl: string;
  alarmStartTime: number;
  alarmEndTime: number;
  severity: string;
  media: Media;
  resolvedAlarm: boolean;
  isTalkDownEnabled: boolean;
  isPastEvent?: boolean;
  isDedupEvent?: boolean;
  location?: Location;
  tap?: number;
  alarm_type?: string;
  alarm_source_system?: string;
  tenantId?: string;
  employee?: string;
  door?: string;
}

export interface Escalation {
  id: string;
  title: string;
  description: string;
  incidentStartTimeUtc: string;
  incidentEndTimeUtc?: string;
  // The alarm that anchors this incident -- on v3 this can be a sibling alarm
  // in the same source group rather than the one being viewed.
  initiatingLocationAlarmId: string;
  // v3 only: the source group this escalation is scoped to.
  sourceGroupId?: string;
  // v3 only: the alarm type the incident was raised for. Together with
  // sourceGroupId this is the scoping key, for every product: one active
  // incident per (group, type). Null only on rows written before the type was
  // added to the key, which `isEscalationInScope` falls back to matching on
  // the group alone.
  locationAlarmTypeId?: string;
}

export interface LLMRecommendation {
  headline: string;
  recommendation: string;
  summary: string;
  version?: number;
}

export interface UnifiedAlarmGroup {
  id: string;
  // v3 only: the group's source group, carried from the location alarm root
  // (event-flow sets it on the RG unified group too). Escalating sends this.
  sourceGroupId?: string;
  // The alarm type's id (not the display name in `locationAlarmType`), used
  // together with sourceGroupId to match incidents to this group.
  locationAlarmTypeId?: string;
  timestamp: string;
  tenantId: string;
  location: Location;
  alarms: UnifiedAlarm[];
  alarmCount?: number;
  status: string;
  severity: string;
  escalations: Escalation[];
  camToAlarmsMap?: Map<string, UnifiedAlarm[]>;
  tapScore?: number;
  resolution?: AlarmResolution;
  uniqueAlarmIds?: Set<string>;
  product?: UnifiedMonitoringProduct;
  llmRecommendation?: LLMRecommendation;
  abTests?: ABTestConfig;
  email_sent_at_utc?: string;
  email_sent_by?: string;
  report_path?: string;
  resolution_comment?: string;
  resolution_reason?: string;
  incident_category?: string;
  resolved_by?: string;
  incident_start_time_utc?: string;
  incident_end_time_utc?: string;
  locationAlarmType?: string;
  // v3 cloud-unification flag from the monitoring WS payload (present on both
  // new-in-queue and get-by-id/refresh responses). When true, this group's REST
  // reads/writes go through the v3 data path (X-Data-Version: v3) and rendering
  // branches on frames presence rather than product.
  unificationEnabled?: boolean;
  // DB partition (YYYYMM) the monitoring WS returns for this group (snake_case on
  // the wire). Sent back alongside unification_enabled on every outbound
  // group-scoped message so the backend hits the right partition directly, and
  // persisted per-group in the URL (`pk`) so it survives a hard refresh.
  partition_key?: string;
}

export interface AlarmGroupsDTO {
  items: UnifiedAlarmGroup[];
  total: number;
  page: number;
  pageSize: number;
  itemsCount: number;
}
export interface EscalatedAlarmGroupsParams {
  dateFrom: number;
  dateTo: number;
  locationIds: number[];
  state: string;
  isEscalated: boolean;
}

export enum MonitoringType {
  SCAN = 'scan',
  LOCATION_ALARM = 'locationAlarm',
}

export interface AlarmCountResponse {
  type: string;
  operatorId: string;
  count: number;
  monitoringType: MonitoringType;
  severity: string;
}

export interface AlarmListResponse {
  type: string;
  payload: UnifiedAlarmGroup[];
  isSuccess?: boolean;
  error?: string;
}

export interface AlarmGroupResponse {
  type: string;
  payload: UnifiedAlarmGroup;
  isSuccess?: boolean;
  error?: string;
}

export interface NeighbourAlarmsRequest {
  startTimestamp: number;
  endTimestamp: number;
  sourceEntityIds: string[];
}

// Every field is optional because the wire omits them: a window with no
// neighbours arrives without `alarms`, and the alarms it does carry can be
// missing their timestamps. Handle the absence rather than trusting the shape
// -- see handleNeighbourAlarmsResponse, where one missing value used to turn
// the timeline bounds into NaN for the rest of the session.
export interface NeighbourAlarmsResponse {
  startTimestamp?: number;
  endTimestamp?: number;
  alarms?: UnifiedAlarm[];
}

export enum UnifiedAlarmMessageType {
  ALARM_COUNT = 'alarm_count',
  LIST_ALARM = 'list_alarm',
  ALARM = 'alarm',
}

export interface UpdateAlarmGroupStateRequest {
  resolution: 'safe' | 'escalation_close' | 'resolved';
  resolutionComment?: string;
  resolutionReason?: string;
  resolutionIncidentCategory?: string;
  operatorId: string;
}

export interface UpdateAlarmGroupStateResponse {
  success: boolean;
  payload: {
    message: string;
    payload: null;
    status: number;
  };
  timestamp: string;
}

export enum SOPViewType {
  WORKFLOW = 'workflow',
  STATIC = 'static',
}

export interface ModifyAlarmGroupUpdatePayload {
  situation?: string;
  situation_description?: string;
}

export interface MultiClickSeekState {
  direction: 1 | -1;
  count: number;
  appliedMs: number;
  timer: ReturnType<typeof setTimeout> | null;
}
